import {
  ModernizedBlockDiagramConfig,
  ModernizedBlockDiagramSourceDataShape,
  ModernizedBlockDiagramViewModelProperties,
  ModernizedBlockDiagramViewStatus,
  ModernizedBlockDiagramViewSettings,
} from './types';
import { Observable, combineLatest } from 'rxjs';
import {
  action$,
  combineReducers,
  dispatchAction,
  reducer,
  streamReducer,
  withNamespace,
} from '@ardoq/rxbeach';
import { isEqual } from 'lodash';
import {
  debounceTime,
  filter,
  map,
  pairwise,
  startWith,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import {
  type APIOrganizationUser,
  type ArdoqId,
  FilterInterfaceFilter,
  FormattingFilter,
  ViewIds,
  type GroupingRule,
} from '@ardoq/api-types';
import {
  createViewStatusReporter,
  registerWhileSubscribed,
  reportDataWorkStarted,
  withDataWork,
} from 'tabview/activeViewsStatus/viewStatusReporter';
import {
  aggregateReferences,
  getAddedAndUpdatedItems,
  getDistinctReferenceKey,
  getGraphNodeCount,
  getGraphEdgeLabelForViewpointMode,
  getIsShowReferenceTypeApplied,
  ScopeDataParams,
} from '@ardoq/graph';
import {
  canBypassLimit,
  isAboveItemLimit,
} from 'tabview/graphComponent/graphItemLimit';
import {
  Features,
  featureOperations,
  type FeatureContext,
} from '@ardoq/features';
import { updateViewProperties, viewSettingsConsts } from '@ardoq/view-settings';
import { updateViewEmptyStateData } from 'appLayout/ardoqStudio/emptyState/actions';
import {
  DEFAULT_EXPECTED_DURATION,
  recordViewModelReady,
  startViewLoadTracking,
} from 'tracking/durationTracking/viewPerformance';
import {
  cloneGraphItemsAsUpdate,
  getPerformanceTrackingData,
  isSeparateReferencesChange,
} from './utils';
import { viewIdToUserFriendlyName } from 'views/metaInfoConsts';
import { getViewpointModeViewSettingsWithChanges$ } from 'views/environmentals/viewSettings/viewpointModeViewSettings$';
import { toViewpointModeMainViewInstanceId } from 'appLayout/mainViewInstanceIds';
import {
  getComponentIdsFromViewModel,
  getViewpointRelationshipsDiagramFlatEmptyState,
} from 'tabview/relationshipDiagrams/utils';
import {
  allActiveOrgUsers$,
  globalViewModelResetSource$,
  orgUsersById$,
  SharedBlockDiagramsRenderingDataSource,
  sharedBlockDiagramsRenderingDataSource$,
} from 'tabview/relationshipDiagrams/shared/sharedGlobalStreams';
import { getFilteredViewSettings$ } from 'views/filteredViewSettings$';
import { componentsChanged$ } from 'streams/components/components$';
import { DEFAULT_DEBOUNCE_TIME } from 'modelInterface/consts';
import { readViewComponentIds } from 'streams/viewComponentIds$';
import {
  collapseAllGroupsReducerForViewpointMode,
  expandAllGroupsReducerForViewpointMode,
  setCollapsedGroupIdsReducerForViewpointMode,
  toggleCollapseGroupReducerForViewpointMode,
} from 'tabview/relationshipDiagrams/viewModel$/toggleCollapseGroupInViewpointMode';
import {
  relationshipDiagramCollapseAllGroups,
  relationshipDiagramExpandAllGroups,
  relationshipDiagramSetCollapsedGroupIds,
  relationshipDiagramToggleCollapseGroup,
} from 'tabview/relationshipDiagrams/actions';
import { updateCollapsedGroupsViewSettingsState } from 'tabview/relationshipDiagrams/viewModel$/collapseGroupUtils';
import { getDefaultRelationshipDiagramsViewInterface } from 'tabview/relationshipDiagrams/viewModel$/getDefaultRelationshipDiagramsViewInterface';
import { scopeDiff$ } from 'scope/scopeDiff$';
import {
  getPureGraphItems,
  getViewpointModeLabelsData,
} from 'tabview/relationshipDiagrams/viewModel$/utils';
import { toViewModel } from 'tabview/relationshipDiagrams/viewModel$/viewModelFromGraph';
import { handleViewpointComponentsUpdate } from 'tabview/relationshipDiagrams/viewModel$/handleViewpointComponentUpdate';
import { getModernizedBlockDiagramRepresentationData } from './view/yFilesExtensions/modernized/modernizedBlockDiagramRepresentationData';
import { filterInterface } from '@ardoq/filter-interface';
import { isPresentationMode } from 'env';
import { getGraphComponentLegendProps } from 'tabview/graphComponent/graphComponentLegendUtils';
import { ScopeDiffState } from '@ardoq/data-model';
import { getRawGraphWithScenarioData } from 'tabview/relationshipsDiagramsViewModel/getRawGraphWithScenarioData';
import { getGraphData } from 'tabview/relationshipsDiagramsViewModel/getGraphData';
import type { ViewpointRelationshipDiagramResetArgs } from 'tabview/relationshipsDiagramsViewModel/types';
import { orgUsersInterface } from 'modelInterface/orgUsers/orgUsersInterface';
import { EMPTY_GRAPH_COMPONENT_LEGEND_PROPS } from 'tabview/relationshipDiagrams/consts';
import { getErrorInfoBoxLabels } from 'locales/getErrorInfoBoxLabels';
import { featureContext$ } from 'streams/featureContext$';
import { groupingRules$ } from 'streams/grouping/groupingRules$';
import { groupingRuleInterface } from 'modelInterface/groupingRules/groupingRuleInterface';
import { filterOperations, usersOperations } from '@ardoq/core';
import { formattingAndLabelRules$ } from 'streams/filters/formattingAndLabelRules$';

const VIEW_ID = ViewIds.MODERNIZED_BLOCK_DIAGRAM;
const VIEW_INSTANCE_ID = toViewpointModeMainViewInstanceId(VIEW_ID);

export const getEmptyModernizedBlockDiagramSourceData = (
  viewInstanceId: string
): ModernizedBlockDiagramSourceDataShape => ({
  ...getViewpointRelationshipsDiagramFlatEmptyState(VIEW_ID),
  uiPropsAndCallbacks: {
    legendProps: EMPTY_GRAPH_COMPONENT_LEGEND_PROPS,
  },
  users: [],
  viewInstanceId,
  viewId: VIEW_ID,
});

export const IGNORE_VIEW_SETTINGS: (keyof ModernizedBlockDiagramViewSettings)[] =
  [
    'separateReferences',
    viewSettingsConsts.IS_LEGEND_ACTIVE,
    'collapsedGroupIds',
  ];

export const cloneViewModelForAnimation = (
  state: ModernizedBlockDiagramSourceDataShape,
  {
    separateReferences,
  }: Pick<ModernizedBlockDiagramViewSettings, 'separateReferences'>
): ModernizedBlockDiagramSourceDataShape => ({
  ...state,
  ...cloneGraphItemsAsUpdate(state),
  viewSettings: { ...state.viewSettings, separateReferences },
});

export const updateScopeDiffStateAndLegendProps = (
  state: ModernizedBlockDiagramSourceDataShape,
  scopeDiffState: ScopeDiffState
): ModernizedBlockDiagramSourceDataShape => {
  const { viewInterface, viewSettings, scopeDataParams, i18nLocale } = state;
  const { componentMap, groupMap, referenceList } = state.rawGraph;

  const formattingFilters = scopeDataParams
    ? scopeDataParams.formattingFilters.filter(
        filterOperations.isStrictlyFormattingFilter
      )
    : filterInterface.getFormattingFilters();

  const users = orgUsersInterface.getAllOrgUsers();

  const legendProps = getGraphComponentLegendProps({
    componentMap,
    groupMap,
    edges: referenceList,
    graphInterface: viewInterface,
    formattingFilters,
    scopeDiff: scopeDiffState.scopeDiff,
    displayShapesInsteadOfIcons: viewSettings.componentStyle === 'shape',
    activeDiffMode: viewSettings.activeDiffMode,
    i18nLocale,
    users,
    isNewConditionalFormatting: true,
  });

  return {
    ...state,
    scopeDiffState,
    uiPropsAndCallbacks: {
      ...state.uiPropsAndCallbacks,
      legendProps,
    },
  };
};

export type ModernizedBlockDiagramSourceDataResetArgs =
  ViewpointRelationshipDiagramResetArgs & {
    isGraphCollapsible?: boolean;
    featureContext?: FeatureContext;
    groupingRules?: GroupingRule[];
    formattingAndLabelRules: (FilterInterfaceFilter | FormattingFilter)[];
    orgUsersById: Record<ArdoqId, APIOrganizationUser>;
  };

export type ModernizedViewModelResetStreamShape =
  ModernizedBlockDiagramSourceDataResetArgs & {
    viewSettings: ModernizedBlockDiagramViewSettings;
  };

export const sourceDataResetReducer = (
  previousState: ModernizedBlockDiagramSourceDataShape,
  sourceDataResetArgs: ModernizedViewModelResetStreamShape
): ModernizedBlockDiagramSourceDataShape =>
  buildSourceData(
    previousState,
    sourceDataResetArgs,
    startViewLoadTracking(
      viewIdToUserFriendlyName[ViewIds.MODERNIZED_BLOCK_DIAGRAM],
      DEFAULT_EXPECTED_DURATION
    )
  );

export const buildSourceData = (
  previousState: ModernizedBlockDiagramSourceDataShape,
  {
    context,
    scenario,
    loadedGraph,
    viewSettings,
    users,
    scopeDataParams,
    i18nLocale,
    isGraphCollapsible,
    groupingRules: groupingRulesInTheAppState = [],
    formattingAndLabelRules: formattingRulesInTheAppState,
    orgUsersById,
  }: ModernizedViewModelResetStreamShape,
  performanceTrackingId: string | null
): ModernizedBlockDiagramSourceDataShape => {
  const resolvedScopeDataParams: ScopeDataParams = scopeDataParams ?? {
    enhancedScopeData: loadedGraph.scopeData,
    formattingFilters: formattingRulesInTheAppState,
    groupingRules: groupingRulesInTheAppState,
    i18nLocale,
    formatUserFieldFn: val =>
      usersOperations.formatUserField(orgUsersById, val),
  };
  const graphModel = resolvedScopeDataParams.enhancedScopeData.graphData;
  const formattingAndLabelRules = resolvedScopeDataParams.formattingFilters;
  const resolvedGroupingRules = resolvedScopeDataParams.groupingRules;

  const {
    viewInstanceId,
    scopeDiffState: { scopeDiff },
  } = previousState;

  const viewInterface = getDefaultRelationshipDiagramsViewInterface(
    resolvedScopeDataParams
  );

  const collapsedGroupIds = new Set(viewSettings.collapsedGroupIds);

  const isInScopeDiffMode = Boolean(scopeDiff);

  const [loadedScenarioData, scenarioRelatedComponents, { showScopeRelated }] =
    scenario;

  const rawGraph = getRawGraphWithScenarioData({
    viewInterface,
    loadedScenarioData,
    scenarioRelatedComponents,
    showScopeRelated,
    graphModel,
    viewId: VIEW_ID,
    loadedGraph,
    isInScopeDiffMode: Boolean(scopeDiff),
    collapsedGroupIds,
    shouldCollapseGraph: true,
    userDefinedGroupingRules: resolvedGroupingRules,
    i18nLocale,
  });

  const {
    componentMap,
    groupMap,
    referenceList: collapsedReferenceList,
    componentIdsOfCollapsedGroups,
    uncollapsedNodes,
    errors,
    hasClones,
  } = rawGraph;

  const getRepresentationData = (componentId: string) =>
    getModernizedBlockDiagramRepresentationData(
      viewInterface,
      componentId,
      viewSettings.componentStyle
    );

  const graphData = getGraphData({
    ...rawGraph,
    viewInterface,
    getRepresentationData,
  });

  const isShowReferenceTypeApplied = getIsShowReferenceTypeApplied(
    viewInterface.getAllReferenceLabelFilters()
  );

  const aggregatedReferenceList = aggregateReferences({
    viewInterface,
    referenceList: collapsedReferenceList,
    getDistinctReferenceKey,
    collapsedGroupIds,
    isInScopeDiffMode,
    urlFieldValuesByReferenceId: graphData.urlFieldValuesByReferenceId,
    getLabel: reference =>
      getGraphEdgeLabelForViewpointMode(
        viewInterface,
        reference,
        i18nLocale,
        isShowReferenceTypeApplied
      ).mainLabel ?? '',
    i18nLocale,
    ...uncollapsedNodes,
  });

  // Edge label data is indexed by graph edge id, hence it shall be composed after
  // reference aggregation (which creates new graph item ids for aggregated edges)
  const { nodeLabelsData, edgeLabelsData } = getViewpointModeLabelsData({
    viewInterface,
    componentMap,
    groupMap,
    rawReferences: aggregatedReferenceList,
    isShowReferenceTypeApplied,
    i18nLocale,
  });

  const { graphNodes, groupNodes, edges } = getPureGraphItems({
    i18nLocale,
    viewInterface,
    groupMap,
    componentMap,
    rawReferences: aggregatedReferenceList,
    componentIdsOfCollapsedGroups,
    rawCollapsedGroupIds: collapsedGroupIds,
    scopeDiff,
    contextComponentId: context.componentId,
    nodeLabelsData,
    edgeLabelsData,
    isGraphCollapsible,
    ...graphData,
  });

  const formattingFilters = formattingAndLabelRules.filter(
    filterOperations.isStrictlyFormattingFilter
  );
  const legendProps = getGraphComponentLegendProps({
    componentMap,
    groupMap,
    edges: aggregatedReferenceList,
    graphInterface: viewInterface,
    formattingFilters,
    scopeDiff,
    displayShapesInsteadOfIcons: viewSettings.componentStyle === 'shape',
    activeDiffMode: viewSettings.activeDiffMode,
    i18nLocale,
    users,
    isNewConditionalFormatting: true,
  });

  return {
    ...previousState,
    canvasToSvg: true,
    viewInterface,
    scopeDataParams: resolvedScopeDataParams,
    componentIdsOfCollapsedGroups,
    expandedStateChangingGroupId: null,
    errors,
    hasClones,
    ...graphData,
    nodes: toViewModel([...graphNodes.values()]),
    edges: toViewModel(edges),
    groups: toViewModel([...groupNodes.values()]),
    viewSettings,
    context,
    graphModel,
    loadedScenarioData,
    scenarioRelatedComponents,
    showScopeRelated,
    rawGraph,
    allGroupIds: Array.from(rawGraph.uncollapsedNodes.groupMap.keys()),
    users,
    getRepresentationData,
    performanceTrackingId,
    viewInstanceId,
    loadedGraph,
    uiPropsAndCallbacks: {
      legendProps,
    },
    viewId: VIEW_ID,
    i18nLocale,
  };
};

type MapToViewPropsArgs = [
  ModernizedBlockDiagramSourceDataShape,
  SharedBlockDiagramsRenderingDataSource,
  ModernizedBlockDiagramConfig,
];

export const mapToViewProps = ([
  viewModel,
  { clearEdges, isContextChange, scenarioName },
  { isPresentationMode: presentationMode, isCanvasPrototype },
]: MapToViewPropsArgs): ModernizedBlockDiagramViewModelProperties => {
  const addedNodes = getAddedAndUpdatedItems(viewModel.nodes);
  const addedEdges = getAddedAndUpdatedItems(viewModel.edges);
  const addedGroups = getAddedAndUpdatedItems(viewModel.groups);
  const graphItemLimitArgs = {
    viewId: VIEW_ID,
    nodeCount: addedNodes.length,
    edgeCount: addedEdges.length,
    presentationMode,
  };
  return {
    ...viewModel,
    zoomComponentId: isContextChange ? viewModel.context.componentId : null,
    clearEdges,
    scenarioName,
    viewInstanceId: viewModel.viewInstanceId,
    isViewpointMode: true,
    isViewEmpty: !getGraphNodeCount(viewModel),
    performanceTrackingId: viewModel.performanceTrackingId,
    isPresentationMode: presentationMode,
    errorInfoBoxLabels: getErrorInfoBoxLabels(viewModel.i18nLocale),
    isAboveLimit: isAboveItemLimit(graphItemLimitArgs),
    canBypass: canBypassLimit(graphItemLimitArgs),
    isEmpty: !addedNodes.length && !addedEdges.length && !addedGroups.length,
    isCanvasPrototype,
    canvasToSvg: viewModel.canvasToSvg ?? false,
    hideZoomControls: !presentationMode,
  };
};

const persistCollapsedGroupIds = (
  previous: ModernizedBlockDiagramSourceDataShape,
  current: ModernizedBlockDiagramSourceDataShape
): void => {
  if (
    current.expandedStateChangingGroupId === null ||
    isEqual(
      previous.viewSettings.collapsedGroupIds,
      current.viewSettings.collapsedGroupIds
    )
  ) {
    return;
  }
  updateCollapsedGroupsViewSettingsState(
    VIEW_ID,
    current.viewSettings.collapsedGroupIds
  );
};

export const getMainViewpointModeViewModel$ = (): Observable<
  ModernizedBlockDiagramViewModelProperties & ModernizedBlockDiagramViewStatus
> => {
  const viewStatusReporter = createViewStatusReporter(VIEW_INSTANCE_ID);
  const viewSettingsWithChanges$ = getViewpointModeViewSettingsWithChanges$(
    VIEW_ID,
    VIEW_INSTANCE_ID
  );
  const filteredSourceDataSettings$ = getFilteredViewSettings$(
    viewSettingsWithChanges$,
    IGNORE_VIEW_SETTINGS
  );
  const unfilteredSettings$: Observable<ModernizedBlockDiagramViewSettings> =
    viewSettingsWithChanges$.pipe(
      map(({ currentSettings }) => currentSettings)
    );

  const separateReferencesViewSetting$ = viewSettingsWithChanges$.pipe(
    filter(isSeparateReferencesChange),
    map(({ currentSettings }) => currentSettings)
  );

  /**
   * We need the reducer to run only when certain view settings change, but we need the
   * complete viewSettings to rebuild the model.
   */
  const sourceDataReset$ = combineLatest([
    globalViewModelResetSource$,
    filteredSourceDataSettings$,
    allActiveOrgUsers$,
    orgUsersById$,
    featureContext$,
    groupingRules$.pipe(
      map(({ groupingRules }) =>
        groupingRules.map(groupingRuleInterface.toInterfaceType)
      )
    ),
    formattingAndLabelRules$,
  ]).pipe(
    withLatestFrom(unfilteredSettings$),
    map(
      ([
        [
          globalAppState,
          _filteredSettings,
          users,
          orgUsersById,
          featureContext,
          groupingRules,
          { rules },
        ],
        viewSettings,
      ]) => ({
        ...globalAppState,
        viewSettings,
        users,
        orgUsersById,
        scopeDataParams: null,
        featureContext,
        groupingRules,
        formattingAndLabelRules: rules,
      })
    ),
    reportDataWorkStarted(viewStatusReporter, 'reset')
  );

  const emptySourceData =
    getEmptyModernizedBlockDiagramSourceData(VIEW_INSTANCE_ID);

  const viewModelSourceData$: Observable<ModernizedBlockDiagramSourceDataShape> =
    action$.pipe(
      withNamespace(VIEW_INSTANCE_ID),
      combineReducers(emptySourceData, [
        streamReducer(sourceDataReset$, sourceDataResetReducer),
        streamReducer(componentsChanged$, handleViewpointComponentsUpdate),
        streamReducer(
          separateReferencesViewSetting$,
          cloneViewModelForAnimation
        ),
        streamReducer(scopeDiff$, updateScopeDiffStateAndLegendProps),
        reducer(
          relationshipDiagramToggleCollapseGroup,
          toggleCollapseGroupReducerForViewpointMode
        ),
        reducer(
          relationshipDiagramExpandAllGroups,
          expandAllGroupsReducerForViewpointMode
        ),
        reducer(
          relationshipDiagramCollapseAllGroups,
          collapseAllGroupsReducerForViewpointMode
        ),
        reducer(
          relationshipDiagramSetCollapsedGroupIds,
          setCollapsedGroupIdsReducerForViewpointMode
        ),
      ]),
      startWith(emptySourceData),
      pairwise(),
      tap(([previousViewModel, viewModel]) => {
        persistCollapsedGroupIds(previousViewModel, viewModel);
        dispatchAction(
          readViewComponentIds({
            [VIEW_ID]: getComponentIdsFromViewModel(viewModel),
          })
        );
      }),
      map(([, viewModel]) => viewModel)
    );

  const presentationMode = isPresentationMode();
  const config$: Observable<ModernizedBlockDiagramConfig> =
    featureContext$.pipe(
      map(featureContext => ({
        isPresentationMode: presentationMode,
        isCanvasPrototype: featureOperations.hasFeature(
          featureContext,
          Features.CANVAS_PROTOTYPE
        ),
      }))
    );

  const renderingData$ = combineLatest([
    viewModelSourceData$,
    sharedBlockDiagramsRenderingDataSource$,
    config$,
  ]).pipe(
    map(mapToViewProps),
    debounceTime(2 * DEFAULT_DEBOUNCE_TIME),
    withDataWork(viewStatusReporter, 'source'),
    tap(viewProperties => {
      const {
        performanceTrackingId,
        viewInterface,
        dataWorkToken: _dataWorkToken,
        ...viewModel
      } = viewProperties;
      if (performanceTrackingId) {
        recordViewModelReady(performanceTrackingId, {
          ...getPerformanceTrackingData(
            viewModel,
            viewInterface.getComponentWorkspaceId
          ),
        });
      }

      dispatchAction(updateViewProperties({ viewId: VIEW_ID, viewProperties }));

      const { isViewEmpty } = viewProperties;
      dispatchAction(
        updateViewEmptyStateData({
          viewEmptyStateData: { isViewEmpty },
          viewId: VIEW_ID,
        })
      );
    })
  );

  return combineLatest([renderingData$, unfilteredSettings$]).pipe(
    map(([viewProps, viewSettings]) => ({
      ...viewProps,
      viewSettings,
      loadingSource: 'activeViewsStatus' as const,
      viewStatusReporter,
    })),
    registerWhileSubscribed(viewStatusReporter)
  );
};
